import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Menu, X, User, Plus, MessageSquare, LogOut, ChevronDown, Globe, ShieldCheck, Heart, ShoppingCart, Wallet, Package, Truck, CalendarDays, Bell } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import NotificationsBell from './NotificationsBell';
import { clsCartCount, CLS_CART_EVENT } from '../lib/classifiedsCart';

export default function Header() {
  const { user, logout } = useAuth();
  const { language, setLanguage, t, tr } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [walletBalance, setWalletBalance] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifUnread, setNotifUnread] = useState(0);
  const [clsCartN, setClsCartN] = useState(0);
  const prevUnreadRef = useRef(null);
  const lastToastIdRef = useRef(null);
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  // Classifieds (C2C) cart lives in localStorage — reflect its count on the
  // main cart icon so buyers see items they added from a listing.
  useEffect(() => {
    const sync = () => setClsCartN(clsCartCount());
    sync();
    window.addEventListener(CLS_CART_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => { window.removeEventListener(CLS_CART_EVENT, sync); window.removeEventListener('storage', sync); };
  }, []);

  useEffect(() => {
    if (!user) { setUnreadCount(0); prevUnreadRef.current = null; return; }
    axios.get(`${API}/wallet`, { withCredentials: true })
      .then(({ data }) => setWalletBalance(data.balance))
      .catch(() => {});
    const fetchNotif = () => axios.get(`${API}/notifications/unread-count`, { withCredentials: true })
      .then(({ data }) => setNotifUnread(data.count || 0))
      .catch(() => {});
    fetchNotif();
    const fetchUnread = async () => {
      try {
        const { data } = await axios.get(`${API}/messages/unread-count`, { withCredentials: true });
        const count = data.count || 0;
        const prev = prevUnreadRef.current;
        setUnreadCount(count);
        // Toast on a genuinely new incoming message (skip first load & the
        // messages page itself, and never toast the same message twice).
        if (prev !== null && count > prev && !pathRef.current.startsWith('/messages')) {
          try {
            const { data: latest } = await axios.get(`${API}/messages/latest-unread`, { withCredentials: true });
            const m = latest.message;
            if (m && m.id !== lastToastIdRef.current) {
              lastToastIdRef.current = m.id;
              toast(`${tr('Jauna ziņa no', 'New message from', 'Новое сообщение от')} ${m.sender_name}`, {
                description: m.content,
                action: {
                  label: tr('Atvērt', 'Open', 'Открыть'),
                  onClick: () => navigate(`/messages?to=${m.sender_id}&name=${encodeURIComponent(m.sender_name)}`),
                },
              });
            }
          } catch (e) { /* ignore */ }
        }
        prevUnreadRef.current = count;
      } catch (e) { /* not logged in */ }
    };
    fetchUnread();
    const t = setInterval(() => { fetchUnread(); fetchNotif(); }, 20000);
    const onVis = () => { if (document.visibilityState === 'visible') { fetchUnread(); fetchNotif(); } };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', onVis); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, language]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const navLinks = [
    { href: '/listings?category=shop', label: t('shops') },
    { href: '/listings?category=service', label: t('services') },
    { href: '/listings?category=event', label: t('events') },
    { href: '/listings?category=project', label: t('projects') },
    { href: '/listings?category=business', label: t('business') },
    { href: '/sludinajumi', label: language === 'lv' ? 'Sludinājumi' : language === 'ru' ? 'Объявления' : 'Classifieds' },
    { href: '/catalog', label: t('catalog') },
    { href: '/blog', label: language === 'lv' ? 'Blogs' : language === 'ru' ? 'Блог' : 'Blog' },
  ];

  // Mobile: primary category nav + secondary links live in the hamburger overflow.
  const overflowLinks = [
    { href: '/listings?category=shop', label: t('shops'), testid: 'mobile-menu-shops-link' },
    { href: '/listings?category=service', label: t('services'), testid: 'mobile-menu-services-link' },
    { href: '/listings?category=event', label: t('events'), testid: 'mobile-menu-events-link' },
    { href: '/listings?category=project', label: t('projects'), testid: 'mobile-menu-projects-link' },
    { href: '/listings?category=business', label: t('business'), testid: 'mobile-menu-business-link' },
    { href: '/sludinajumi', label: language === 'lv' ? 'Sludinājumi' : language === 'ru' ? 'Объявления' : 'Classifieds', testid: 'mobile-menu-classifieds-link' },
    { href: '/catalog', label: t('catalog'), testid: 'mobile-menu-catalog-link' },
    { href: '/blog', label: language === 'lv' ? 'Blogs' : language === 'ru' ? 'Блог' : 'Blog', testid: 'mobile-menu-blog-link' },
  ];

  return (
    <>
    <header className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-black/5" data-testid="header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <span className="text-2xl font-bold text-[#121212] font-outfit">KoLV</span>
            <span className="text-sm text-[#5C5C5C] hidden sm:block">2026</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="text-[#5C5C5C] hover:text-[#D97736] transition-colors font-figtree text-sm"
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right Side */}
          <div className="flex items-center gap-3">
            {/* Language Switcher — guests only (logged-in users change it in Profile) */}
            {!user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="px-3 py-1.5 rounded-full bg-[#F3F2EE] text-sm font-medium text-[#121212] hover:bg-[#E5E4E0] transition-colors flex items-center gap-1"
                  data-testid="language-switcher"
                >
                  {language.toUpperCase()}
                  <ChevronDown className="w-3 h-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[120px]">
                <DropdownMenuItem
                  onClick={() => setLanguage('lv')}
                  className={language === 'lv' ? 'bg-[#F3F2EE] font-medium' : ''}
                  data-testid="lang-option-lv"
                >
                  🇱🇻 Latviešu
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setLanguage('en')}
                  className={language === 'en' ? 'bg-[#F3F2EE] font-medium' : ''}
                  data-testid="lang-option-en"
                >
                  🇬🇧 English
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setLanguage('ru')}
                  className={language === 'ru' ? 'bg-[#F3F2EE] font-medium' : ''}
                  data-testid="lang-option-ru"
                >
                  🇷🇺 Русский
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            )}

            {user ? (
              <div className="flex items-center gap-2">
                <Link to="/create-listing" className="hidden sm:block">
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-[#D97736] text-[#D97736] hover:bg-[#D97736] hover:text-white"
                    data-testid="create-listing-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    {t('createListing')}
                  </Button>
                </Link>

                <Link to="/cart" className="relative" data-testid="cart-icon">
                  <Button variant="ghost" size="sm" className="text-[#5C5C5C] hover:text-[#D97736]">
                    <ShoppingCart className="w-5 h-5" />
                  </Button>
                  {clsCartN > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 text-[10px] font-bold text-white bg-[#2F7E79] rounded-full px-1.5 min-w-[18px] text-center leading-[18px]" data-testid="cart-cls-badge">
                      {clsCartN}
                    </span>
                  )}
                </Link>

                {/* Wallet & notifications live in the profile menu on mobile —
                    shown as header icons only on desktop (md+) to keep the
                    mobile top bar clean. */}
                <Link to="/wallet" data-testid="wallet-icon" className="hidden md:block">
                  <Button variant="ghost" size="sm" className="text-[#5C5C5C] hover:text-[#4A6B53] relative">
                    <Wallet className="w-5 h-5" />
                    {walletBalance !== null && walletBalance > 0 && (
                      <span className="absolute -top-1 -right-1 text-[10px] font-bold text-[#4A6B53] bg-green-100 rounded-full px-1 min-w-[18px] text-center" data-testid="wallet-balance-badge">
                        {walletBalance >= 1000 ? `${(walletBalance/1000).toFixed(0)}k` : `€${walletBalance.toFixed(0)}`}
                      </span>
                    )}
                  </Button>
                </Link>

                <span className="hidden md:block">
                  <NotificationsBell />
                </span>

                <Link to="/messages" className="relative p-2 rounded-full hover:bg-[#F3F2EE] transition-colors" data-testid="header-messages-btn" aria-label={t('messages')}>
                  <MessageSquare className="w-5 h-5 text-[#121212]" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 text-[10px] font-bold text-white bg-red-500 rounded-full px-1.5 min-w-[18px] text-center leading-[18px]" data-testid="header-messages-unread-badge">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Link>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="relative flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F3F2EE] hover:bg-[#E5E4E0] transition-colors"
                      data-testid="user-menu-trigger"
                    >
                      <div className="w-7 h-7 rounded-full bg-[#D97736] flex items-center justify-center text-white text-sm font-medium">
                        {user.name?.charAt(0).toUpperCase()}
                      </div>
                      <ChevronDown className="w-4 h-4 text-[#5C5C5C]" />
                      {/* Combined attention dot (notifications) on mobile where
                          the bell is hidden. */}
                      {notifUnread > 0 && (
                        <span className="md:hidden absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-red-500 rounded-full ring-2 ring-white" data-testid="user-menu-attention-dot" />
                      )}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-60">
                    {/* Mans konts */}
                    <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-[#9A9A9A] font-semibold">
                      {tr('Mans konts', 'My account', 'Мой аккаунт')}
                    </DropdownMenuLabel>
                    <DropdownMenuItem asChild>
                      <Link to="/profile" className="flex items-center gap-2" data-testid="profile-link">
                        <User className="w-4 h-4 text-[#5C5C5C]" />
                        {t('profile')}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to={`/lietotajs/${user._id}`} className="flex items-center gap-2" data-testid="my-shop-link">
                        <Globe className="w-4 h-4 text-[#5C5C5C]" />
                        {tr('Publiskais profils', 'Public profile', 'Публичный профиль')}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/wallet" className="flex items-center gap-2" data-testid="wallet-link">
                        <Wallet className="w-4 h-4 text-[#4A6B53]" />
                        {tr('Maciņš', 'Wallet', 'Кошелёк')}
                        {walletBalance !== null && (
                          <span className="ml-auto text-xs font-bold text-[#4A6B53]" data-testid="wallet-menu-balance">
                            €{Number(walletBalance).toFixed(2)}
                          </span>
                        )}
                      </Link>
                    </DropdownMenuItem>

                    <DropdownMenuSeparator />
                    {/* Darījumi */}
                    <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-[#9A9A9A] font-semibold">
                      {tr('Darījumi', 'Transactions', 'Сделки')}
                    </DropdownMenuLabel>
                    <DropdownMenuItem asChild>
                      <Link to="/orders" className="flex items-center gap-2" data-testid="my-orders-link">
                        <Package className="w-4 h-4 text-[#5C5C5C]" />
                        {tr('Mani pasūtījumi', 'My orders', 'Мои заказы')}
                      </Link>
                    </DropdownMenuItem>
                    {user.is_seller && (
                      <DropdownMenuItem asChild>
                        <Link to="/seller/orders" className="flex items-center gap-2" data-testid="seller-orders-link">
                          <Truck className="w-4 h-4 text-[#5C5C5C]" />
                          {tr('Saņemtie pasūtījumi', 'Incoming orders', 'Полученные заказы')}
                        </Link>
                      </DropdownMenuItem>
                    )}

                    <DropdownMenuSeparator />
                    {/* Aktivitātes */}
                    <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-[#9A9A9A] font-semibold">
                      {tr('Aktivitātes', 'Activity', 'Активность')}
                    </DropdownMenuLabel>
                    <DropdownMenuItem asChild>
                      <Link to="/messages" className="flex items-center gap-2" data-testid="messages-link">
                        <MessageSquare className="w-4 h-4 text-[#5C5C5C]" />
                        {t('messages')}
                        {unreadCount > 0 && (
                          <span className="ml-auto bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center" data-testid="unread-badge">
                            {unreadCount}
                          </span>
                        )}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/notifications" className="flex items-center gap-2" data-testid="notifications-link">
                        <Bell className="w-4 h-4 text-[#5C5C5C]" />
                        {tr('Paziņojumi', 'Notifications', 'Уведомления')}
                        {notifUnread > 0 && (
                          <span className="ml-auto bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center" data-testid="notifications-menu-badge">
                            {notifUnread > 9 ? '9+' : notifUnread}
                          </span>
                        )}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/favorites" className="flex items-center gap-2" data-testid="favorites-link">
                        <Heart className="w-4 h-4 text-[#5C5C5C]" />
                        {tr('Izlase', 'Favorites', 'Избранное')}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/calendar" className="flex items-center gap-2" data-testid="calendar-link">
                        <CalendarDays className="w-4 h-4 text-[#5C5C5C]" />
                        {tr('Mans kalendārs', 'My calendar', 'Мой календарь')}
                      </Link>
                    </DropdownMenuItem>

                    <DropdownMenuSeparator />
                    {user.role === 'admin' && (
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="flex items-center gap-2 text-[#D97736]" data-testid="admin-link">
                          <ShieldCheck className="w-4 h-4" />
                          Admin
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                      <LogOut className="w-4 h-4 mr-2" />
                      {t('logout')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ) : (
              <div className="flex items-center gap-1.5 sm:gap-2">
                <Link to="/login">
                  <Button variant="ghost" size="sm" className="px-2.5 sm:px-3 text-xs sm:text-sm" data-testid="login-btn">
                    {t('login')}
                  </Button>
                </Link>
                <Link to="/register">
                  <Button size="sm" className="bg-[#D97736] hover:bg-[#C06528] text-white px-2.5 sm:px-3 text-xs sm:text-sm" data-testid="register-btn">
                    {t('register')}
                  </Button>
                </Link>
              </div>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2"
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-[#E5E4E0]" data-testid="mobile-menu">
            <nav className="flex flex-col gap-2">
              {overflowLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-4 py-2 text-[#5C5C5C] hover:text-[#D97736] hover:bg-[#F3F2EE] rounded-lg transition-colors"
                  data-testid={link.testid}
                >
                  {link.label}
                </Link>
              ))}
              {user && (
                <Link
                  to="/create-listing"
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-4 py-2 text-[#D97736] hover:bg-[#F3F2EE] rounded-lg transition-colors font-medium"
                >
                  {t('createListing')}
                </Link>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
    </>
  );
}
